import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  darken,
  Divider,
  styled,
  TextField,
  Typography,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useEffect, useState } from 'react';
import isFinite from 'lodash/isFinite';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import {
  type Product,
  type ProductFamily,
  type ServiceStatusInfo,
  type PricingTier,
  PricingModel,
  ProductCode,
  ServiceStatus,
} from '../types';
import { shouldDisplayRibbon } from '../utils/shouldDisplayRibbon';
import getRibbonVariables from '../utils/getRibbonVariables';
import { getRibbonText } from '../utils/getRibbonText';
import { getColorConfig } from '../utils/getColorConfig';
import PlatformFeatures from './PlatformFeatures';
import ProductPricing from './ProductPricing';
import { sanitizeHtmlTagsAndWhitespace } from '../utils/sanitizeHtmlTagsAndWhitespace';
import { FEATURE_FLAG_KEYS } from '../../../config/growthbook';
import { getProductButtonConfig } from '../utils/getProductButtonConfig';
import { getCurrentServiceStatusInfo } from '../utils/getCurrentServiceStatusInfo';
import { Cart, Invoice } from '../features/self-serve/types/types';
import { useHistory } from 'react-router-dom';
import { useNotifications } from '../../../_lib/notifications/useNotifications';
import { canModifyProduct } from '../utils/canModifyProduct';
import { isNonSelfServeProductCode } from '../utils/productCodes';
import { Text } from '@cowbell/ui';

const ProductCard = ({
  hasExpiredPolicy = false,
  hasRenewedPolicy = false,
  idx,
  processingProductId,
  product,
  serviceStatusInfos,
  isServiceStatusInfoLoading,
  productFamily,
  isPoolTenant = false,
  isProductInCart = false,
  cart,
  ctaasServiceStatus,
  isEligibleForFreeCtaas,
  handleAddProductToCart,
  handleAddProductToOpportunity,
  revenue,
  products = [],
  invoices = [],
}: {
  hasExpiredPolicy?: boolean;
  hasRenewedPolicy?: boolean;
  idx: number;
  processingProductId: string | null;
  product: Product;
  serviceStatusInfos: ServiceStatusInfo[];
  isServiceStatusInfoLoading: boolean;
  productFamily?: ProductFamily;
  isPoolTenant?: boolean;
  isProductInCart: boolean;
  cart: Cart | null;
  ctaasServiceStatus?: ServiceStatus;
  isEligibleForFreeCtaas: boolean;
  handleAddProductToCart: (
    cart: Cart | null,
    product: Product,
    quantity: number,
    hasConfirmedReplacement: boolean
  ) => Promise<void>;
  handleAddProductToOpportunity: (product: Product) => Promise<void>;
  revenue?: number | null;
  products: Product[];
  invoices: Invoice[];
}) => {
  const theme = useTheme();
  const [quantity, setQuantity] = useState(1);
  const [quantityError, setQuantityError] = useState('');
  const [selectedTier, setSelectedTier] = useState<PricingTier | null>(null);
  const currentServiceStatusInfo = getCurrentServiceStatusInfo(
    product,
    serviceStatusInfos
  );
  const crsEnableSelfServe = useFeatureIsOn(
    FEATURE_FLAG_KEYS.CRS_ENABLE_SELF_SERVE
  );
  const history = useHistory();
  const notifications = useNotifications();

  const { pricingData } = product;
  const { priceDetails } = pricingData || {};

  useEffect(() => {
    if (
      pricingData?.pricingModel === PricingModel.Block &&
      pricingData.priceDetails.tiers?.length &&
      !selectedTier
    ) {
      const tiers = pricingData.priceDetails.tiers;

      if (tiers.length > 0) {
        setSelectedTier(tiers[0]);
      }
    }
  }, [pricingData, selectedTier]);

  // For CTS_BST products, use ctaasServiceStatus if available, otherwise use serviceStatusInfos
  const currentServiceStatus =
    product.code === ProductCode.CTS_BST && ctaasServiceStatus !== undefined
      ? ctaasServiceStatus
      : currentServiceStatusInfo?.status ?? ServiceStatus.NotActivated;
  const ribbonText = getRibbonText(product.ribbon, currentServiceStatus);

  const isRibbonVisible = shouldDisplayRibbon({
    uniqueCode: product.code,
    hasExpiredPolicy,
    hasRenewedPolicy,
    ribbon: product.ribbon,
    serviceStatus: currentServiceStatus,
  });

  const ribbonVariables = getRibbonVariables(theme, ribbonText);
  const colorConfig = getColorConfig(theme);
  const platformFeatures =
    typeof product?.features === 'string'
      ? sanitizeHtmlTagsAndWhitespace(product?.features)
      : '';

  const isThisProductProcessing = processingProductId === product.id;
  const isDisabled = isThisProductProcessing || isServiceStatusInfoLoading;
  const productButtonConfig = getProductButtonConfig({
    product,
    productFamily,
    isPoolTenant,
    crsEnableSelfServe,
    serviceStatusInfos,
    cart,
    isEligibleForFreeCtaas,
    history,
    handleAddProductToCart,
    handleAddProductToOpportunity,
    notifications,
    isDisabled,
    quantity,
    selectedTier,
    products,
    invoices,
  });

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numValue = parseInt(value, 10);

    if (value === '' || numValue < 1) {
      setQuantityError('Quantity must be at least 1');
      setQuantity(value === '' ? 0 : numValue);
    } else {
      setQuantityError('');
      setQuantity(numValue);
    }
  };

  const isQuantityFieldVisible = canModifyProduct({
    isProductInCart,
    isPoolTenant,
    product,
    crsEnableSelfServe,
    currentServiceStatusInfo,
    requiredPricingModel: PricingModel.Standard,
    checkTiers: false,
  });

  const isTierSelectorVisible = canModifyProduct({
    isProductInCart,
    isPoolTenant,
    product,
    crsEnableSelfServe,
    currentServiceStatusInfo,
    requiredPricingModel: PricingModel.Block,
    checkTiers: true,
  });

  const isNonSelfServeProduct = isNonSelfServeProductCode(product.code);
  const isTabletopIsoProduct = product.code === ProductCode.TTE_ISO;

  return (
    <StyledCard key={product.id} data-testid="service-pricing-card">
      {isRibbonVisible && (
        <StyledRibbon
          bgColor={ribbonVariables.bgColor}
          textColor={ribbonVariables.textColor}
        >
          {ribbonText}
        </StyledRibbon>
      )}
      <CardContent sx={{ p: 0, borderRadius: '8px' }}>
        <Typography
          variant="h5"
          component="h2"
          className="text-left"
          sx={{
            fontWeight: 700,
            borderLeft: `8px solid ${colorConfig?.[idx]?.bgColor}`,
            color: colorConfig?.[idx]?.bgColor || theme.palette.common.white,
            fontSize: '2rem',
            padding: '1rem',
            margin: '1rem 0',
            marginBottom: 0,
          }}
        >
          {product.name}
        </Typography>
        {isFinite(revenue) &&
          isFinite(product.revenueDiscountThreshold) &&
          revenue < product.revenueDiscountThreshold && (
            <StyledDiscountBadge role="status" aria-label="Discount available">
              <StyledCircle borderColor={theme.palette.common.green1} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.common.white,
                  fontSize: '0.875rem',
                  lineHeight: 1.4,
                }}
              >
                Discount Available
              </Typography>
            </StyledDiscountBadge>
          )}

        {crsEnableSelfServe && !isNonSelfServeProduct && (
          <Box sx={{ px: 2, py: 1 }}>
            <ProductPricing
              product={product}
              colorConfig={colorConfig?.[idx]}
              selectedTier={selectedTier}
              setSelectedTier={setSelectedTier}
              quantity={quantity}
              setQuantity={setQuantity}
            />
          </Box>
        )}

        <StyledKeyFeatureText>Key Features</StyledKeyFeatureText>
        <Divider sx={{ margin: '0 2rem' }} />
        <PlatformFeatures
          features={platformFeatures}
          color={colorConfig?.[idx]?.bgColor}
        />
      </CardContent>
      <CardActions
        className="p-4"
        sx={{
          justifyContent: 'center',
          marginTop: 'auto',
          padding: '2rem',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {isQuantityFieldVisible && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <StyledQuantityField
              type="number"
              label={product.quantityType ?? 'Quantity'}
              value={quantity}
              onChange={handleQuantityChange}
              error={!!quantityError}
              helperText={quantityError}
              required
              inputProps={{ min: 1, max: 99999 }}
              data-testid="quantity-input"
              disabled={isProductInCart}
            />
          </Box>
        )}
        {isTierSelectorVisible && (
          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontSize: '1.25rem', top: 2 }}>
              Select User Range
            </InputLabel>
            <Select
              inputProps={{ sx: { border: 'none' } }}
              value={selectedTier?.lowerBound || ''}
              label="Select User Range"
              onChange={(e) => {
                const tier = priceDetails.tiers?.find(
                  (t) => t.lowerBound === e.target.value
                );
                if (tier) setSelectedTier(tier);
              }}
            >
              {Array.isArray(priceDetails.tiers) &&
                priceDetails.tiers?.map((tier) => (
                  <MenuItem key={tier.lowerBound} value={tier.lowerBound}>
                    <Typography variant="body2" fontWeight={500}>
                      {tier.lowerBound} - {tier.upperBound - 1}{' '}
                      {product.quantityType}s
                    </Typography>
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}
        {!isPoolTenant && isTabletopIsoProduct && (
          <Text size="sm" ta="center" w="100%">
            Sold together with a Self Service plan
          </Text>
        )}
        {!isPoolTenant && (
          <StyledOutlineButton
            variant="outlined"
            bgColor={colorConfig?.[idx]?.bgColor}
            textColor={colorConfig?.[idx]?.textColor}
            onClick={productButtonConfig?.onClick}
            disabled={productButtonConfig?.disabled || !!quantityError}
            {...(productButtonConfig?.helperText && {
              startIcon: <FontAwesomeIcon icon={faCircleInfo} />,
            })}
          >
            {isServiceStatusInfoLoading ? (
              <CircularProgress size={24} />
            ) : (
              productButtonConfig?.text
            )}
            {isThisProductProcessing && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </StyledOutlineButton>
        )}
        {productButtonConfig?.helperText && (
          <StyledHelperText role="status" aria-live="polite">
            {productButtonConfig.helperText}
          </StyledHelperText>
        )}
      </CardActions>
    </StyledCard>
  );
};

const StyledCard = styled(Card)(({ theme }) => ({
  width: '100%',
  display: 'grid',
  alignContent: 'space-between',
  border: `1px solid ${theme.palette.primary.border9}`,
  borderRadius: '1rem',
  cursor: 'pointer',
  minHeight: '20rem',
  background: theme.palette.primary.cardBackground,
  boxShadow: '0px 2px 2px 0px #00000040',
  transition: 'transform 0.3s ease-in-out',
  position: 'relative',
  overflow: 'visible',
}));

const StyledKeyFeatureText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.25rem',
  color: theme.palette.primary.keyFeatureText,
  padding: '0.5rem 2rem',
  margin: 0,
}));

const StyledOutlineButton = styled(Button)(
  ({ textColor, bgColor }: { textColor: string; bgColor: string }) => ({
    color: textColor,
    backgroundColor: bgColor,
    border: `2px solid transparent`,
    marginTop: '0rem',
    padding: '0.75rem 1rem',
    fontSize: '1.16667rem',
    fontWeight: 700,
    borderRadius: '4px',
    width: '100%',

    '&:hover': {
      border: `2px solid ${darken(bgColor, 0.3)}`,
      backgroundColor: bgColor,
      color: textColor,
      opacity: 1,
    },

    '&:disabled': {
      color: textColor,
      backgroundColor: '#C3C3C3',
      opacity: 0.75,
    },
  })
);

const StyledRibbon = styled('div')(
  ({ bgColor, textColor }: { bgColor: string; textColor: string }) => ({
    '--f': '.5em' /* control the folded part*/,
    '--r': '.8em' /* control the ribbon shape */,

    position: 'absolute',
    top: '-1em',
    right: 'calc(-1*var(--f))',
    'padding-inline': '1em',
    lineHeight: 1.8,
    background: bgColor,
    'border-bottom': 'var(--f) solid #0005',
    'border-left': 'var(--r) solid #0000',
    'clip-path': `polygon(0 0,100% 0,100% calc(100% - var(--f)),calc(100% - var(--f)) 100%,
      calc(100% - var(--f)) calc(100% - var(--f)),0 calc(100% - var(--f)),
      var(--r) calc(50% - var(--f)/2))`,
    zIndex: 1,
    'font-size': '1.25rem',
    color: textColor,
    'font-weight': 'bold',
  })
);

const StyledQuantityField = styled(TextField)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiInputBase-input': {
    padding: '12px',
    fontSize: '0.875rem',
    textAlign: 'center',
    border: 'none',
  },
  '& .MuiInputLabel-root': {
    fontSize: '1.25rem',
    top: 2,
  },
}));

const StyledDiscountBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '16px',
  right: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.5rem 1rem',
  backgroundColor: theme.palette.common.green1,
  borderRadius: '1rem',
  boxShadow: '0px 2px 8px rgba(4, 128, 0, 0.25)',
  zIndex: 2,
  animation: 'pulse 2s ease-in-out infinite',
  '@keyframes pulse': {
    '0%, 100%': {
      transform: 'scale(1)',
      boxShadow: '0px 2px 8px rgba(4, 128, 0, 0.25)',
    },
    '50%': {
      transform: 'scale(1.02)',
      boxShadow: '0px 4px 12px rgba(4, 128, 0, 0.35)',
    },
  },
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0px 4px 16px rgba(4, 128, 0, 0.4)',
    transition: 'all 0.2s ease-in-out',
  },
}));

const StyledCircle = styled('div')<{ borderColor: string }>(
  ({ borderColor, theme }) => ({
    width: '0.75rem',
    height: '0.75rem',
    backgroundColor: theme.palette.common.white,
    borderRadius: '50%',
    flexShrink: 0,
    boxShadow: `0 0 0 2px ${borderColor}`,
  })
);

const StyledHelperText = styled(FormHelperText)(({ theme }) => ({
  margin: '0rem',
  textAlign: 'center',
  fontSize: '0.875rem',
  fontWeight: 400,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  width: '100%',
  opacity: 0.85,
}));

export default ProductCard;
